import { useCallback, useEffect, useMemo, useState } from "react";
import FilterBar from "../components/FilterBar";
import Table from "../components/Table";

const PAGE_SIZE_OPTIONS = [12, 24, 48];

function TablePage({
  config,
  onRowClick,
  hideViewAction = false,
  renderActions,
  rows: rowsProp,
  renderSearch,
  selectedFilters,
  onFilterChange,
  filtersAllowMultiple = false,
  actionsRightAligned = false,
  hideQuickActions = false,
  stackOnMobile = false,
}) {
  const rows = useMemo(() => {
    const source = rowsProp ?? config?.rows;
    return Array.isArray(source) ? source : [];
  }, [config?.rows, rowsProp]);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage) || 1);
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  useEffect(() => {
    setPage((prev) => {
      const next = Math.min(prev, totalPages);
      return next < 1 ? 1 : next;
    });
  }, [totalPages]);

  const paginatedRows = useMemo(() => {
    if (totalRows === 0) return [];
    const start = (currentPage - 1) * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [currentPage, rows, rowsPerPage, totalRows]);

  const pageStart = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const pageEnd = totalRows === 0 ? 0 : Math.min(currentPage * rowsPerPage, totalRows);
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + maxButtons - 1);
    const normalizedStart = Math.max(1, end - maxButtons + 1);
    for (let i = normalizedStart; i <= end; i += 1) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  const handlePageChange = useCallback(
    (nextPage) => {
      setPage((prev) => {
        const target = Math.min(Math.max(nextPage, 1), totalPages);
        return target === prev ? prev : target;
      });
    },
    [totalPages]
  );

  const handleRowsPerPageChange = useCallback((event) => {
    const value = Number(event?.target?.value) || PAGE_SIZE_OPTIONS[0];
    setRowsPerPage(value);
    setPage(1);
  }, []);

  if (!config) return null;

  const footerLabel = totalRows === 0 ? "No results to display" : `Showing ${pageStart}-${pageEnd} of ${totalRows}`;
  const filterBar = (
    <FilterBar options={config.filters} selected={selectedFilters} onSelect={onFilterChange} allowMultiple={filtersAllowMultiple} />
  );
  const actionButtons = renderActions ? <div className="inline-actions">{renderActions()}</div> : null;
  const tableWrapClassName = stackOnMobile ? "table-wrap stackable" : "table-wrap";

  return (
    <section className="panel wide">
      <header className="panel-header">
        <div className="panel-title">{hideQuickActions ? null : <button className="ghost-btn small">Quick Actions ▾</button>}</div>
        <div className="panel-actions">
          {actionsRightAligned ? (
            <>
              {filterBar}
              {actionButtons}
            </>
          ) : (
            <>
              {actionButtons}
              {filterBar}
            </>
          )}
        </div>
      </header>

      {renderSearch ? renderSearch() : null}

      <div className={tableWrapClassName}>
        <Table
          columns={config.columns}
          rows={paginatedRows}
          onRowClick={onRowClick}
          hideViewAction={hideViewAction}
          stackOnMobile={stackOnMobile}
        />
      </div>

      <footer className="panel-footer">
        <div className="muted small">{footerLabel}</div>
        <div className="pagination">
          <button type="button" onClick={() => handlePageChange(1)} disabled={currentPage === 1 || totalRows === 0}>
            «
          </button>
          <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || totalRows === 0}>
            ‹
          </button>
          {pageNumbers.map((pageNumber) => (
            <button
              type="button"
              key={pageNumber}
              className={pageNumber === currentPage ? "current" : ""}
              onClick={() => handlePageChange(pageNumber)}
              disabled={pageNumber === currentPage || totalRows === 0}
            >
              {pageNumber}
            </button>
          ))}
          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalRows === 0}
          >
            ›
          </button>
          <button
            type="button"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalRows === 0}
          >
            »
          </button>
        </div>
        <div className="muted small">
          Show{" "}
          <select aria-label="rows per page" value={rowsPerPage} onChange={handleRowsPerPageChange}>
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option value={option} key={option}>
                {option}
              </option>
            ))}
          </select>{" "}
          / page
        </div>
      </footer>
    </section>
  );
}

export default TablePage;
